import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useContext } from "react";
import { GiCircuitry } from "react-icons/gi";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { useMessaging } from "../../master/species/hooks/useSubmit";
import { useTranslation } from "react-i18next";
import useUser from "../../../hooks/useUser";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/loader/Loader";
import { HOME } from "../../../enums/UrlPaths";
import { useIsMobile, useIsTablet } from "../../../utils/Functions";
import logger from "../../../utils/logger";

const AiAssistant = () => {
  const { t } = useTranslation();
  const { roles, isLoading, currentClinic } = useUser();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const submittion = useMessaging(
    null,
    "/saps/ai_assistant/",
    "some_query_get",
  );
  // The AI assistant is enabled per clinic by a master
  React.useEffect(() => {
    if (!isLoading && !roles.master && !currentClinic.aiAssistantEnabled) {
      navigate(HOME);
    }
  }, [isLoading, roles.master, currentClinic.aiAssistantEnabled]);

  // Keep messages in memory
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // User message
    const userMessage = {
      id: Date.now().toString(),
      text: inputValue,
      user: true,
      timeStamp: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    try {
      // Send to AI
      const submitData = { query: userMessage.text, modelId: 1 };
      const res = await submittion.submitCallback(submitData);

      // Always check if response exists
      if (res && res.data) {
        const aiResponse = {
          id: (Date.now() + 1).toString(),
          text: res.data,
          user: false,
          timeStamp: new Date().toLocaleTimeString(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }
    } catch (err) {
      logger.error("AI request failed", err);
    }
  };

  if (isLoading) {
    return <Loader />;
  }
  return (
    <PageContentWrapper noSideMargin={true} sideMargin={isMobile ? 0 : 5}>
      <Box
        sx={{
          height: { xs: "85vh", lg: "82vh", xl: "85vh" },
          display: "flex",
          flexDirection: "column",
          p: isMobile ? 0 : 2,
        }}
      >
        <ResponseBody messages={messages} isLoading={submittion.loading} />

        <Paper elevation={0} sx={{ p: 2 }}>
          <form onSubmit={handleSendMessage}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t("AIPlaceholder")}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                multiline
                maxRows={4}
                disabled={submittion.loading}
              />
              <IconButton
                color="primary"
                type="submit"
                disabled={!inputValue.trim() || submittion.loading}
                sx={{ alignSelf: "flex-end" }}
                title={t("Send")}
              >
                <SendIcon />
              </IconButton>
            </Box>
            {!isMobile && !isTablet && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {t("AIInstructions")}
              </Typography>
            )}
          </form>
        </Paper>
      </Box>
    </PageContentWrapper>
  );
};

export default AiAssistant;

function ResponseBody({ messages = [], isLoading }) {
  const { mode } = useContext(ThemeContext);
  const { t } = useTranslation();

  if (!messages.length) {
    return (
      <Wrapper>
        <Stack
          height="100%"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          <GiCircuitry
            size="20vh"
            color={mode === "dark" ? "#3030308c" : "#a7a7a78c"}
            style={{ borderRadius: "50%" }}
          />
          <Stack justifyContent="center" alignItems="center" spacing={1}>
            <Typography
              color={mode === "dark" ? "GrayText" : "#383838ff"}
              textAlign="center"
            >
              {t("AIWelcomeTitle")}
            </Typography>
            <Typography
              color={mode === "dark" ? "GrayText" : "#383838ff"}
              textAlign="center"
            >
              {t("AIWelcomeSubtitle")}
            </Typography>
          </Stack>
        </Stack>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <List sx={{ flex: 1 }}>
        {messages.map((message) => (
          <ListItem
            key={message.id}
            sx={{
              display: "flex",
              justifyContent: message.user ? "flex-end" : "flex-start",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            {!message.user && (
              <Avatar sx={{ mr: 1, bgcolor: "primary.main" }}>
                <GiCircuitry color="white" />
              </Avatar>
            )}
            <Paper
              elevation={1}
              sx={{
                p: 2,
                maxWidth: "70%",
                bgcolor: message.user ? "primary.main" : "grey.100",
                color: message.user ? "white" : "black",
                borderRadius: 2,
              }}
            >
              {message.user ? (
                <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
                  {message.text}
                </Typography>
              ) : (
                formatAIResponse(message.text)
              )}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  opacity: 0.7,
                  color: message.user ? "white" : "GrayText",
                }}
              >
                {message.timeStamp}
              </Typography>
            </Paper>

            {message.user && (
              <Avatar sx={{ ml: 1, bgcolor: "primary.main" }}>
                <PersonIcon sx={{ color: "white" }} />
              </Avatar>
            )}
          </ListItem>
        ))}

        {isLoading && (
          <ListItem
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Avatar sx={{ mr: 1, bgcolor: "primary.main" }}>
              <GiCircuitry color="white" />
            </Avatar>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t("AIThinking")}
            </Typography>
          </ListItem>
        )}
      </List>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2,
        mb: 2,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        scrollbarWidth: "none",
      }}
    >
      {children}
    </Paper>
  );
}

function formatAIResponse(text) {
  if (!text) return null;

  const lines = text.split("\n").filter((line) => line.trim() !== "");
  return lines.map((line, index) => {
    // Headings (###)
    if (line.startsWith("###")) {
      const heading = line.replace(/###\s*/, "");
      return (
        <Typography
          key={index}
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mt: 1, overflowWrap: "anywhere" }}
        >
          {heading}
        </Typography>
      );
    }

    // List items (-)
    if (line.startsWith("-")) {
      // Bold inside list
      const parts = line
        .replace(/^-+\s*/, "")
        .split(/\*\*(.*?)\*\*/g) // split by bold
        .map((part, i, arr) =>
          i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
        );

      return (
        <Typography
          key={index}
          component="div"
          sx={{ ml: 2, overflowWrap: "anywhere" }}
        >
          • {parts}
        </Typography>
      );
    }

    // Plain text
    return (
      <Typography
        key={index}
        variant="body2"
        sx={{ mt: 0.5, overflowWrap: "anywhere" }}
      >
        {line}
      </Typography>
    );
  });
}
